import { API_URL } from '../config/constants';
import { getTokenAuth } from '../utils/authUtils';
import { 
  CONDITION_REPORT_TYPES, 
  CONDITION_ACTIONS, 
  USER_ROLES,
  PHOTO_TYPES 
} from '../constants/conditionReports';

/**
 * Service pour les appels API des rapports d'état des lieux
 */
class ConditionReportApiService {

  constructor() {
    this.baseUrl = `${API_URL}/condition-reports`;
  }

  /**
   * Headers par défaut avec authentification
   */
  _getHeaders(includeContentType = true) {
    const headers = {};
    
    const token = getTokenAuth();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  }

  /**
   * Gérer les erreurs de réponse
   */
  async _handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP Error: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Créer un nouveau rapport d'état
   * @param {Object} reportData - Données du rapport
   * @returns {Promise<Object>}
   */
  async createReport(reportData) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this._getHeaders(),
        body: JSON.stringify(reportData)
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error creating condition report:', error);
      throw error;
    }
  }

  /**
   * Récupérer un rapport par ID
   * @param {string} reportId - ID du rapport
   * @returns {Promise<Object>}
   */
  async getReportById(reportId) {
    try {
      const response = await fetch(`${this.baseUrl}/${reportId}`, {
        method: 'GET',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error fetching condition report:', error);
      throw error;
    }
  }

  /**
   * Récupérer tous les rapports d'une réservation
   * @param {string} reservationId - ID de la réservation
   * @returns {Promise<Object>}
   */
  async getReportsByReservation(reservationId) {
    try {
      const response = await fetch(`${this.baseUrl}/reservation/${reservationId}`, {
        method: 'GET',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error fetching reports by reservation:', error);
      throw error;
    }
  }

  /**
   * Récupérer les rapports avec filtres
   * @param {Object} filters - Filtres de recherche
   * @returns {Promise<Object>}
   */
  async getReportsWithFilters(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const url = queryParams.toString() 
        ? `${this.baseUrl}?${queryParams.toString()}`
        : this.baseUrl;

      const response = await fetch(url, {
        method: 'GET',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error fetching reports with filters:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour un rapport
   * @param {string} reportId - ID du rapport
   * @param {Object} updateData - Données à mettre à jour
   * @returns {Promise<Object>}
   */
  async updateReport(reportId, updateData) {
    try {
      const response = await fetch(`${this.baseUrl}/${reportId}`, {
        method: 'PUT',
        headers: this._getHeaders(),
        body: JSON.stringify(updateData)
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error updating condition report:', error);
      throw error;
    }
  }

  /**
   * Supprimer un rapport
   * @param {string} reportId - ID du rapport
   * @returns {Promise<Object>}
   */
  async deleteReport(reportId) {
    try {
      const response = await fetch(`${this.baseUrl}/${reportId}`, {
        method: 'DELETE',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error deleting condition report:', error);
      throw error;
    }
  }

  /**
   * Obtenir les statistiques des rapports
   * @param {Object} filters - Filtres pour les statistiques
   * @returns {Promise<Object>}
   */
  async getStatistics(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const url = queryParams.toString() 
        ? `${this.baseUrl}/statistics?${queryParams.toString()}`
        : `${this.baseUrl}/statistics`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  // =================== MÉTHODES PHOTOS ===================

  /**
   * Ajouter une photo à un rapport
   * @param {string} reportId - ID du rapport
   * @param {File} photoFile - Fichier photo
   * @param {Object} photoData - Données de la photo
   * @returns {Promise<Object>}
   */
  async addPhotoToReport(reportId, photoFile, photoData = {}) {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      
      if (photoData.photo_type) {
        formData.append('photo_type', photoData.photo_type);
      }
      if (photoData.description) {
        formData.append('description', photoData.description);
      }

      const response = await fetch(`${this.baseUrl}/${reportId}/photos`, {
        method: 'POST',
        headers: this._getHeaders(false), // Pas de Content-Type pour FormData
        body: formData
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error adding photo to report:', error);
      throw error;
    }
  }

  /**
   * Récupérer toutes les photos d'un rapport
   * @param {string} reportId - ID du rapport
   * @returns {Promise<Object>}
   */
  async getPhotosByReport(reportId) {
    try {
      const response = await fetch(`${this.baseUrl}/${reportId}/photos`, {
        method: 'GET',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error fetching photos by report:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour une photo
   * @param {string} photoId - ID de la photo
   * @param {Object} updateData - Données à mettre à jour
   * @returns {Promise<Object>}
   */
  async updatePhoto(photoId, updateData) {
    try {
      const response = await fetch(`${this.baseUrl}/photos/${photoId}`, {
        method: 'PUT',
        headers: this._getHeaders(),
        body: JSON.stringify(updateData)
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error updating photo:', error);
      throw error;
    }
  }

  /**
   * Réorganiser l'ordre des photos
   * @param {string} reportId - ID du rapport
   * @param {string[]} photoIds - Tableau des IDs dans le nouvel ordre
   * @returns {Promise<Object>}
   */
  async reorderPhotos(reportId, photoIds) {
    try {
      const response = await fetch(`${this.baseUrl}/${reportId}/photos/reorder`, {
        method: 'PUT',
        headers: this._getHeaders(),
        body: JSON.stringify({ photoIds })
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error reordering photos:', error);
      throw error;
    }
  }

  /**
   * Supprimer une photo
   * @param {string} photoId - ID de la photo
   * @returns {Promise<Object>}
   */
  async deletePhoto(photoId) {
    try {
      const response = await fetch(`${this.baseUrl}/photos/${photoId}`, {
        method: 'DELETE',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error;
    }
  }

  /**
   * Récupérer les infos d'une photo
   * @param {string} photoId - ID de la photo
   * @returns {Promise<Object>}
   */
  async getPhotoById(photoId) {
    try {
      const response = await fetch(`${this.baseUrl}/photos/${photoId}`, {
        method: 'GET',
        headers: this._getHeaders()
      });

      return await this._handleResponse(response);
    } catch (error) {
      console.error('Error fetching photo:', error);
      throw error;
    }
  }

  // =================== MÉTHODES UTILITAIRES ===================

  /**
   * Valider un fichier photo avant upload
   * @param {File} file - Fichier à valider
   * @returns {Object} - Résultat de validation
   */
  validatePhotoFile(file) {
    const result = {
      isValid: true,
      errors: []
    };

    // Vérifier la taille
    if (file.size > 10 * 1024 * 1024) { // 10MB
      result.isValid = false;
      result.errors.push('La taille du fichier ne peut pas dépasser 10MB');
    }

    // Vérifier le type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      result.isValid = false;
      result.errors.push('Format non supporté. Formats acceptés: JPG, PNG, WebP');
    }

    return result;
  }

  /**
   * Valider les données d'un rapport
   * @param {Object} reportData - Données à valider
   * @returns {Object} - Résultat de validation
   */
  validateReportData(reportData) {
    const result = {
      isValid: true,
      errors: []
    };

    // Vérifications obligatoires
    if (!reportData.reservation_id) {
      result.isValid = false;
      result.errors.push('ID de réservation obligatoire');
    }

    if (!reportData.type || !Object.values(CONDITION_REPORT_TYPES).includes(reportData.type)) {
      result.isValid = false;
      result.errors.push('Type de rapport invalide');
    }

    if (!reportData.action || !Object.values(CONDITION_ACTIONS).includes(reportData.action)) {
      result.isValid = false;
      result.errors.push('Action invalide');
    }

    if (!reportData.user_role || !Object.values(USER_ROLES).includes(reportData.user_role)) {
      result.isValid = false;
      result.errors.push('Rôle utilisateur invalide');
    }

    // Validation métier pour les refus
    if (reportData.action === CONDITION_ACTIONS.REFUSE && !reportData.refusal_reason) {
      result.isValid = false;
      result.errors.push('La raison du refus est obligatoire');
    }

    // Validation des longueurs
    if (reportData.notes && reportData.notes.length > 2000) {
      result.isValid = false;
      result.errors.push('Les notes ne peuvent pas dépasser 2000 caractères');
    }

    if (reportData.refusal_reason && reportData.refusal_reason.length > 1000) {
      result.isValid = false;
      result.errors.push('La raison de refus ne peut pas dépasser 1000 caractères');
    }

    return result;
  }

  /**
   * Récupérer tous les condition reports d'une réservation
   * @param {string} reservationId - ID de la réservation
   * @returns {Promise<Object>}
   */
  async getByReservation(reservationId) {
    if (!reservationId) {
      throw new Error('L\'ID de réservation est requis');
    }

    const response = await fetch(`${this.baseUrl}/reservation/${reservationId}`, {
      method: 'GET',
      headers: this._getHeaders()
    });

    return await this._handleResponse(response);
  }

  /**
   * Récupérer un condition report avec toutes ses photos
   * @param {string} conditionReportId - ID du condition report
   * @returns {Promise<Object>}
   */
  async getWithPhotos(conditionReportId) {
    if (!conditionReportId) {
      throw new Error('L\'ID du condition report est requis');
    }

    const response = await fetch(`${this.baseUrl}/${conditionReportId}/photos`, {
      method: 'GET',
      headers: this._getHeaders()
    });

    return await this._handleResponse(response);
  }
}

// Export d'une instance singleton
const conditionReportApiService = new ConditionReportApiService();
export default conditionReportApiService;
